import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import {
  useGetAllBuildingsById,
  useGetAllLocationsDropdown,
  useGetAllRoles,
} from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import { useGetAllUsers } from "../../hooks/user/query";
import debounce from "lodash.debounce";
import { EXPORT_PAGE_SIZE, PAGE_SIZE } from "../../constants/common";
import i18n from "../../i18n";
import { Button } from "../button";
import { ExportUsersForExcelPDFBody } from "../../hooks/user/interface";

interface SearchUsersCardProps {
  setAllUsers: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportUsersForExcelPDFBody | undefined>
  >;
  pageSize: number;
}

const SearchUsersCardSchema = Yup.object({
  firstName: Yup.string(),
  lastName: Yup.string(),
  username: Yup.string(),
  number: Yup.string().matches(
    /^\d{1,14}$/,
    i18n.language === "en" ? "Enter a valid number." : "أدخل رقماً صحيحاً."
  ),
  role: Yup.string(),
  location: Yup.string(),
  building: Yup.string(),
});

const initialState = {
  firstName: "",
  lastName: "",
  number: "",
  username: "",
  role: "",
  location: "",
  building: "",
};

const SearchUsersCard = ({
  setAllUsers,
  page,
  setPages,
  search,
  setCount,
  setExportBody,
  pageSize,
}: SearchUsersCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useFormWithSchema(SearchUsersCardSchema);
  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: allRoles } = useGetAllRoles();

  const roles = useMemo(
    () => mapToOptions("s_RoleName", "s_RoleNameAr", "n_RoleID", allRoles),
    [allRoles]
  );

  const role = watch("role");
  const location = watch("location");
  const building = watch("building");
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const username = watch("username");
  const number = watch("number");

  const [debouncedFirstName, setDebouncedFirstName] = useState(firstName);
  const [debouncedLastName, setDebouncedLastName] = useState(lastName);
  const [debouncedUsername, setDebouncedUsername] = useState(username);
  const [debouncedNumber, setDebouncedNumber] = useState(number);

  const handleFirstNameChange = useCallback(
    debounce((value) => setDebouncedFirstName(value), 500),
    []
  );

  const handleLastNameChange = useCallback(
    debounce((value) => setDebouncedLastName(value), 500),
    []
  );

  const handleUsernameChange = useCallback(
    debounce((value) => setDebouncedUsername(value), 500),
    []
  );

  const handleNumberChange = useCallback(
    debounce((value) => setDebouncedNumber(value), 500),
    []
  );

  useEffect(() => {
    handleFirstNameChange(firstName);
    return () => handleFirstNameChange.cancel();
  }, [firstName, handleFirstNameChange]);

  useEffect(() => {
    handleLastNameChange(lastName);
    return () => handleLastNameChange.cancel();
  }, [handleLastNameChange, lastName]);

  useEffect(() => {
    handleUsernameChange(username);
    return () => handleUsernameChange.cancel();
  }, [handleUsernameChange, username]);

  useEffect(() => {
    handleNumberChange(number);
    return () => handleNumberChange.cancel();
  }, [handleNumberChange, number]);

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: location ? location.toString() : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings
          )
        : [],
    [allBuildings]
  );

  const { data: users } = useGetAllUsers({
    S_FirstName: debouncedFirstName,
    S_LastName: debouncedLastName,
    S_UserName: debouncedUsername,
    S_Mobile: debouncedNumber,
    N_RoleId: Number(role) || 0,
    N_LocationId: Number(location) || 0,
    n_BuildingID: building ? Number(building) : 0,
    N_PageSize: pageSize,
    N_PageNumber: page,
    S_Search: search,
  });

  useEffect(() => {
    setExportBody({
      S_FirstName: debouncedFirstName,
      S_LastName: debouncedLastName,
      S_UserName: debouncedUsername,
      S_Mobile: debouncedNumber,
      N_RoleId: Number(role) || 0,
      N_LocationId: Number(location) || 0,
      n_BuildingID: building ? Number(building) : 0,
      N_PageSize: EXPORT_PAGE_SIZE,
      N_PageNumber: page,
      S_Search: search,
    });
  }, [
    pageSize,
    building,
    debouncedFirstName,
    debouncedLastName,
    debouncedNumber,
    debouncedUsername,
    location,
    page,
    role,
    search,
    setExportBody,
  ]);

  useEffect(() => {
    if (users) {
      setAllUsers(users.users || []);
      setPages(users.pagination?.pages || 1);
      setCount(users.pagination?.count || 0);
    }
  }, [setAllUsers, setPages, users, setCount]);

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 lg:gap-4">
              <Controller
                name="firstName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("firstName")}
                    onChange={(e) => {
                      onChange(e);
                      handleFirstNameChange(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("lastName")}
                    onChange={(e) => {
                      onChange(e);
                      handleLastNameChange(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="username"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("username")}
                    onChange={(e) => {
                      onChange(e);
                      handleUsernameChange(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="number"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("phoneNumber")}
                    onChange={(e) => {
                      onChange(e);
                      handleNumberChange(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterPhoneNumber")}</p>}
                  />
                )}
              />

              <Controller
                name="role"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={roles}
                    value={
                      value
                        ? roles?.find((item) => item.value === value)?.labelEn
                        : ""
                    }
                    onChange={(value) => {
                      onChange(value);
                    }}
                    error={errors?.[name]?.message}
                    label={t("role")}
                  />
                )}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setValue("building", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />
            </div>
          </form>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchUsersCard);
